import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ToastrService } from "ngx-toastr";
import { catchError, tap } from "rxjs/operators";
import { AppSpinnerModule } from "src/app/shared/modules/app-spinner.module";
import { Client } from "src/app/shared/models/client.model";
import { EmailInviteNew } from "src/app/shared/models/email-invite.model";
import { EmailInviteService } from "src/app/shared/services/email-invite.service";
import { MatSelect } from "@angular/material/select";

@UntilDestroy()
@Component({
    selector: 'support-add-user-modal',
    templateUrl: './support-add-user-modal.component.html',
    styleUrls: ['./support-add-user-modal.component.scss'],
    standalone: false
})
export class SupportAddUserModalComponent implements OnInit, AfterViewInit {
  @ViewChild('clientSelect') clientSelect: MatSelect;
  @ViewChild('clientInput') clientInput: ElementRef;

  clientId: number;
  clientList: Client[];
  clientName: string;
  addUserForm: UntypedFormGroup;
  addUsersArray: UntypedFormArray;
  filteredClientList: Client[];
  accountRoles: any[] = [
    { key: "CLIENTADMIN", value: "Client Admin" },
    { key: "ORDERMANAGER", value: "Order Manager" },
    { key: "SETTLEMENTAGENT", value: "Settlement Agent" },
    { key: "SIGNINGAGENT", value: "Signing Agent" }
  ]

  constructor(
    private readonly modal: NgbActiveModal,
    private readonly emailService: EmailInviteService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly spinner: AppSpinnerModule,
    private readonly toastr: ToastrService,
  ) {}

  ngOnInit() {
    this.addUserForm = new UntypedFormGroup({
      users: this.formBuilder.array([])
    });
    this.addUsersArray = this.addUserForm.get('users') as UntypedFormArray;
    this.addUser();
    this.resetFilteredClientList();
  }

  ngAfterViewInit(): void {
    this.clientSelect.openedChange.pipe(
      untilDestroyed(this)
    ).subscribe((opened) => {
      if (opened) {
        this.resetFilteredClientList();
        this.addUserForm.get("users.0.clientFilter")?.setValue("");
        this.clientInput.nativeElement.focus();
      }
    });
  }

  addUser() {
    const userForm = this.formBuilder.group({
      name: ['', Validators.maxLength(200)],
      targetEmailAddress: ['', [
        Validators.email,
        Validators.maxLength(200),
        Validators.required
      ]],
      roles: [[], Validators.required],
      clientId: ['', Validators.required],
      clientFilter: ['']
    });
    this.addUsersArray.push(userForm);
  }

  removeUserInvite(index: number) {
    this.addUsersArray.removeAt(index);
  }

  close(): void {
    this.modal.dismiss();
  }

  sendInvites() {
    if (this.addUserForm.invalid) {
      this.markFormControlsAsDirty(this.addUserForm);
      this.toastr.warning("Please correct all errors.", "Attention");
      return;
    }

    this.spinner.show();

    for (const userGroup of this.addUsersArray.controls) {
      const emailInviteModel = this.createEmailInviteModel(userGroup.value);
      this.emailService.saveInvite(emailInviteModel)
        .pipe(
          tap(() => {
            this.modal.close();
            this.spinner.hide();

            this.toastr.success("Invite Sent!");
          }),
          catchError(() => {
            this.modal.close();
            this.spinner.hide();

            this.toastr.error("Error: Invite Not Sent");

            return [];
          }),
          untilDestroyed(this)
        )
        .subscribe();
    }
  }

  filterClients(i) {
    const filterValue = this.addUserForm.get(`users.${i}.clientFilter`)?.value;
    this.filteredClientList = this.clientList.filter(client => client.clientName.toLowerCase().includes(filterValue.toLowerCase()));
  }

  resetFilteredClientList() {
    this.filteredClientList = this.clientList;
  }

  private createEmailInviteModel(userGroup): EmailInviteNew {
    const emailInviteNew: EmailInviteNew = {
      clientId: userGroup.clientId,
      targetEmailAddress: userGroup.targetEmailAddress,
      roles: userGroup.roles,
      name: userGroup.name
    };
    return emailInviteNew;
  }

  private markFormControlsAsDirty(formGroup: UntypedFormGroup | UntypedFormArray) {
    Object.values(formGroup.controls).forEach(control => {
        if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
          this.markFormControlsAsDirty(control);
        } else {
          control.markAsDirty();
        }
      });
  }
}
